import ExcelJS from 'exceljs'
import Export from './Export'
import AuthService from './auth'
import { DateTime } from 'luxon'

export default class Worksheet {
  static createTotalEarningsSheet = ({
    dateRange,
    selectedProfessional,
    sortedEarnings,
    bonuses,
    discounts
  }) => {
    const workbook = new ExcelJS.Workbook ()
    const title = 'Rendimentos do periodo'
    const worksheet = workbook.addWorksheet (title)
    const startDate = DateTime.fromISO (dateRange[0]).toFormat ('dd/MM/yyyy')
    const endDate = DateTime.fromISO (dateRange[1]).toFormat ('dd/MM/yyyy')
    const description = `(${startDate} a ${endDate}) - ${selectedProfessional.label}`
    const fileName = `${title} ${description.replace (/\//g, '-')}`
    const allProfessionalsSelected = selectedProfessional.value === 'all_professionals'
    const professionalLabel = allProfessionalsSelected ? 'dos profissionais' : 'do profissional'
    const canViewTotalEarnings = AuthService.hasPermission ('financial.total_earnings')
    const canViewBusinessEarnings = AuthService.hasPermission ('financial.business_earnings')

    worksheet.columns = [
      {header: description, key: 'day', width: description.length + 2},
      canViewTotalEarnings ? {header: 'Total bruto', key: 'totalEarnings', width: 25} : null,
      {header: 'Comissões', key: 'professionalEarnings', width: 25},
      bonuses.length > 0 ? {header: 'Bônus', key: 'bonusTotal', width: 25} : null,
      discounts.length > 0 ? {header: 'Descontos', key: 'discountTotal', width: 25} : null,
      {header: `Total ${professionalLabel}`, key: 'professionalTotalEarnings', width: 25},
      canViewBusinessEarnings ? {header: 'Total Líquido', key: 'businessEarnings', width: 25} : null
    ].filter (o => o)

    worksheet.addRows (sortedEarnings.map (earning => ({
      ...earning,
      bonusTotal: earning.bonusTotal !== 0 ? earning.bonusTotal : '-',
      discountTotal: earning.discountTotal !== 0 ? earning.discountTotal : '-'
    })))

    worksheet.eachRow ((row, rowNumber) => {
      row.height = rowNumber > 1 ? 20 : 30
      row.eachCell ((cell, colNumber) => {
        cell.font = {name: 'Arial', bold: true, color: {argb: rowNumber > 1 ? '000000' : 'FFFFFF'}}
        cell.alignment = {vertical: 'middle', horizontal: 'center'}
        if (colNumber > 1) {
          cell.numFmt = '"R$ "#,##0.00;"R$ "#,##0.00'
          if (rowNumber > 1) cell.font.color = {
            argb: cell.value >= 0 ? '44CF6C' : isNaN (cell.value) ? '000000' : 'FF3939'
          }
        }
        if (rowNumber > 1) cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: 'EEEEEE'}}
        else cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: 'FFC100'}}
      })
    })
    
    Export.downloadWorkSheet (workbook, fileName)
  }

  static createDetailedEarningsSheet ({
    dateRange,
    earningOptionList,
    typeOptionList,
    typeOption,
    selectedProfessional,
    sortedEarnings,
    bonuses,
    discounts
  }) {
    const workbook = new ExcelJS.Workbook ()
    const title = typeOption.label
    const allProfessionalsSelected = selectedProfessional.value === 'all_professionals'
    const professionalLabel = allProfessionalsSelected ? 'dos profissionais' : 'do profissional'
    const startDate = DateTime.fromISO (dateRange[0]).toFormat ('dd/MM/yyyy')
    const endDate = DateTime.fromISO (dateRange[1]).toFormat ('dd/MM/yyyy')
    const description = `(${startDate} a ${endDate})` + (
      typeOption.value !== 'professionals' ? ` - ${selectedProfessional.label}` : ''
    )
    const fileName = `${title} ${description.replace (/\//g, '-')}`
    const earningOptions = typeOption.value === 'bill'
      ? earningOptionList
      : typeOptionList.filter (option => option.value === typeOption.value)
    const canViewTotalEarnings = AuthService.hasPermission ('financial.total_earnings')
    const canViewBusinessEarnings = AuthService.hasPermission ('financial.business_earnings')

    earningOptions.forEach (option => {
      const worksheet = workbook.addWorksheet (option.label)

      switch (option.value) {
        case 'professionals': worksheet.columns = [
          {header: option.label, key: 'name', width: 25},
          canViewTotalEarnings ? {header: 'Total bruto', key: 'totalEarnings', width: 25} : null,
          {header: 'Comissões', key: 'professionalEarnings', width: 25},
          bonuses.length > 0 ? {header: 'Bônus', key: 'bonusTotal', width: 25} : null,
          discounts.length > 0 ? {header: 'Descontos', key: 'discountTotal', width: 25} : null,
          {header: `Total ${professionalLabel}`, key: 'professionalTotalEarnings', width: 25},
          canViewBusinessEarnings ? {header: 'Total Líquido', key: 'businessEarnings', width: 25} : null
        ].filter (o => o); break
        case 'bonuses': 
        case 'discounts': worksheet.columns = [
          {header: 'Data', key: 'date', width: 25},
          {header: 'Descrição', key: 'description', width: 50},
          {header: 'Valor', key: 'value', width: 25}
        ].filter (o => o); break
        default: worksheet.columns = [
          {header: option.label, key: 'name', width: 50},
          {header: 'Quantidade', key: 'qtd', width: 25},
          canViewTotalEarnings ? {header: 'Total bruto', key: 'totalEarnings', width: 25} : null,
          {header: 'Comissões', key: 'professionalEarnings', width: 25},
          canViewBusinessEarnings ? {header: 'Total Líquido', key: 'businessEarnings', width: 25} : null
        ].filter (o => o); break
      }
  
      worksheet.addRows (sortedEarnings[option.value].map (e => ({
        ...e, ...e.date && {date: DateTime.fromISO (e.date).toFormat ('dd/MM/yyyy')}
      })))
  
      worksheet.eachRow ((row, rowNumber) => {
        row.height = rowNumber > 1 ? 20 : 30
        row.eachCell ((cell, colNumber) => {
          cell.font = {name: 'Arial', bold: true, color: {argb: rowNumber > 1 ? '000000' : 'FFFFFF'}}
          cell.alignment = {vertical: 'middle', horizontal: 'center'}
          if (colNumber > 2 || (option.value === 'professionals' && colNumber > 1)) {
            cell.numFmt = '"R$ "#,##0.00;"R$ "#,##0.00'
            if (rowNumber > 1) cell.font.color = {argb: cell.value >= 0 ? '44CF6C' : 'FF3939'}
          }
          if (rowNumber > 1) cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: 'EEEEEE'}}
          else cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: 'FFC100'}}
        })
      })
    })
    
    Export.downloadWorkSheet (workbook, fileName)
  }
}
